import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import { api, generateCRUDInterface } from "aldoo-ra/API"
import UserPlatformAPI from "../../../user-api"

/**
 * Modal for creating a new platform user
 */
const CreateUserModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    status: 'active'
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Initialize CRUD interface for User model
  const crudAPI = api("crud")
  const userCRUD = generateCRUDInterface(crudAPI, "User")

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name?.trim()) {
      newErrors.name = "Name is required"
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address"
    }


    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      setLoading(true)

      const userData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        status: formData.status
      }

      // Check if user with this email already exists
      const emailCheckResponse = await UserPlatformAPI.CheckEmailExists(userData.email)

      if (!emailCheckResponse.result) {
        showNotification({
          message: emailCheckResponse.error || "Failed to validate email",
          className: "bg-admin_error text-white",
          duration: 4000
        })
        return
      }

      if (emailCheckResponse.exists) {
        setErrors({ email: "A user with this email address already exists" })
        return
      }

      const response = await UserPlatformAPI.CreateUser(userData)

      if (response.result) {
        showNotification({
          message: `User "${userData.name}" created successfully`,
          className: "bg-admin_success text-white",
          duration: 3000
        })

        // Reset form
        setFormData({ name: '', email: '', status: 'active' })
        setErrors({})
        onSuccess && onSuccess(response.data)
        onClose()
      } else {
        showNotification({
          message: response.error || "Failed to create user",
          className: "bg-admin_error text-white",
          duration: 4000
        })
      }
    } catch (error) {
      showNotification({
        message: error.message || "Failed to create user",
        className: "bg-admin_error text-white",
        duration: 4000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({ name: '', email: '', status: 'active' })
    setErrors({})
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New User"
      variant="admin_centered"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Typography variant="admin_txt1_dim">
            Full Name
          </Typography>

          <Input
            variant="admin"
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter full name"
            disabled={loading}
            error={errors.name}
          />
        </div>

        <div>
          <Typography variant="admin_txt1_dim">
            Email Address
          </Typography>

          <Input
            variant="admin"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="Enter email address"
            disabled={loading}
            error={errors.email}
          />
        </div>


        <div>
          <Typography variant="admin_txt1_dim">
            Status
          </Typography>

          <DropdownSelect
            variant="admin"
            value={[{ value: formData.status, name: formData.status.charAt(0).toUpperCase() + formData.status.slice(1) }]}
            onChange={(selected) => {
              if (selected && selected.length > 0) {
                handleChange('status', selected[0].value)
              }
            }}
            options={[
              { value: 'active', name: 'Active' },
              { value: 'inactive', name: 'Inactive' },
              { value: 'suspended', name: 'Suspended' }
            ]}
            multiSelection={false}
            className="w-full"
            disabled={loading}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="admin_secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="admin_primary"
            loading={loading}
          >
            Create User
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default CreateUserModal
